import React, { useEffect, useState, useRef, Component } from 'react';
import { Sidebar } from '../components/Sidebar';
import { Header } from '../components/Header';
import { ClockIcon, EditIcon, SparklesIcon, PhoneIcon, MailIcon, MessageCircleIcon, PlayIcon, PauseIcon, Volume2Icon, BuildingIcon, CarIcon, RefreshCwIcon, XIcon, PhoneCallIcon, StopCircleIcon, MicIcon, UserIcon, MapPinIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { WhatsAppChatModal } from '../components/whatsapp/WhatsAppChatModal';
import { LeadDetailOverlay } from '../components/leads/LeadDetailOverlay';
import { WhatsAppLead } from '../types/whatsapp';
import { useViewMode } from '../context/ViewModeContext';
import { ALL_LEADS, Lead } from '../data/mockData';
// Audio Recording Player Component
function RecordingPlayer({
  duration,
  leadId
}: {
  duration: string;
  leadId: number;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setAudioDuration(audio.duration);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);
  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };
  const formatTime = (time: number) => {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  const progress = audioDuration > 0 ? currentTime / audioDuration * 100 : 0;
  return <div className="inline-flex items-center gap-2 bg-blue-50 rounded-full px-3 py-1.5 border border-blue-100" data-id="element-3091">
      <button onClick={togglePlay} className="text-blue-600 hover:text-blue-700 transition-colors" data-id="element-3092">
        {isPlaying ? <PauseIcon size={14} data-id="element-3093" /> : <PlayIcon size={14} fill="currentColor" data-id="element-3094" />}
      </button>
      <span className="text-xs text-gray-600 min-w-[70px]" data-id="element-3095">
        {formatTime(currentTime)} / {duration}
      </span>
      <div className="w-16 h-1.5 bg-blue-200 rounded-full relative overflow-hidden" data-id="element-3096">
        <div className="absolute left-0 top-0 h-full bg-blue-500 rounded-full transition-all duration-100" style={{
        width: `${progress}%`
      }} data-id="element-3097" />
      </div>
      <button className="text-gray-500 hover:text-blue-600 transition-colors" data-id="element-3098">
        <Volume2Icon size={14} data-id="element-3099" />
      </button>
      <audio ref={audioRef} src="https://call-em-quick.lovable.app/Call.m4a" preload="metadata" data-id="element-3100" />
    </div>;
}
// Voice Agent Overlay Component
function VoiceAgentOverlay({
  isOpen,
  onClose,
  leadName
}: {
  isOpen: boolean;
  onClose: () => void;
  leadName: string;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  useEffect(() => {
    if (isOpen && audioRef.current) {
      audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
    }
    if (!isOpen && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      setCurrentTime(0);
    }
  }, [isOpen]);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);
  const togglePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };
  const handleStop = () => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setIsPlaying(false);
    setCurrentTime(0);
  };
  const handleClose = () => {
    handleStop();
    onClose();
  };
  const formatTime = (time: number) => `${Math.floor(time / 60)}:${Math.floor(time % 60).toString().padStart(2, '0')}`;
  const progress = duration > 0 ? currentTime / duration * 100 : 0;
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center" onClick={handleClose} data-id="element-3101">
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-3102">
        <div className="p-6 pb-4" data-id="element-3103">
          <div className="flex items-center justify-between mb-6" data-id="element-3104">
            <div className="flex items-center gap-3" data-id="element-3105">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center" data-id="element-3106">
                <MicIcon size={20} className="text-blue-400" data-id="element-3107" />
              </div>
              <div data-id="element-3108">
                <h3 className="text-white font-semibold" data-id="element-3109">Voice Agent</h3>
                <p className="text-slate-400 text-xs" data-id="element-3110">AI-Powered Call</p>
              </div>
            </div>
            <button onClick={handleClose} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/20 transition-colors" data-id="element-3111">
              <XIcon size={16} data-id="element-3112" />
            </button>
          </div>
          <div className="text-center mb-8" data-id="element-3113">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 mx-auto mb-4 flex items-center justify-center shadow-lg shadow-blue-500/30" data-id="element-3114">
              <UserIcon size={36} className="text-white" data-id="element-3115" />
            </div>
            <h2 className="text-white text-xl font-semibold mb-1" data-id="element-3116">
              {leadName}
            </h2>
            <p className="text-slate-400 text-sm" data-id="element-3117">
              {isPlaying ? 'Call in progress...' : 'Call paused'}
            </p>
          </div>
          <div className="flex items-center justify-center gap-1 h-12 mb-6" data-id="element-3118">
            {[...Array(20)].map((_, i) => <div key={i} className={`w-1 rounded-full transition-all duration-150 ${isPlaying ? 'bg-blue-400' : 'bg-slate-600'}`} style={{
            height: isPlaying ? `${Math.random() * 100}%` : '20%'
          }} data-id="element-3119" />)}
          </div>
          <div className="mb-4" data-id="element-3120">
            <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden" data-id="element-3121">
              <div className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full transition-all duration-100" style={{
              width: `${progress}%`
            }} data-id="element-3122" />
            </div>
            <div className="flex justify-between mt-2 text-xs text-slate-400" data-id="element-3123">
              <span data-id="element-3124">{formatTime(currentTime)}</span>
              <span data-id="element-3125">{formatTime(duration)}</span>
            </div>
          </div>
        </div>
        <div className="bg-slate-800/50 p-6 flex items-center justify-center gap-6" data-id="element-3126">
          <button onClick={handleStop} className="w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center text-red-400 hover:bg-red-500/30 hover:text-red-300 transition-colors" data-id="element-3127">
            <StopCircleIcon size={28} data-id="element-3128" />
          </button>
          <button onClick={togglePlayPause} className="w-16 h-16 rounded-full bg-blue-500 flex items-center justify-center text-white hover:bg-blue-400 transition-colors shadow-lg shadow-blue-500/40" data-id="element-3129">
            {isPlaying ? <PauseIcon size={32} data-id="element-3130" /> : <PlayIcon size={32} fill="white" data-id="element-3131" />}
          </button>
          <button onClick={handleClose} className="w-14 h-14 rounded-full bg-slate-700 flex items-center justify-center text-slate-300 hover:bg-slate-600 transition-colors" data-id="element-3132">
            <PhoneIcon size={24} className="rotate-[135deg]" data-id="element-3133" />
          </button>
        </div>
        <audio ref={audioRef} src="https://call-em-quick.lovable.app/Call.m4a" preload="metadata" data-id="element-3134" />
      </div>
    </div>;
}
function OutboundCallPopup({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center" onClick={onClose} data-id="element-3135">
      <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()} data-id="element-3136">
        <div className="flex items-center justify-between mb-4" data-id="element-3137">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center" data-id="element-3138">
            <PhoneCallIcon size={24} className="text-green-600" data-id="element-3139" />
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors" data-id="element-3140">
            <XIcon size={18} data-id="element-3141" />
          </button>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-3142">
          Outbound Call Initiated
        </h3>
        <p className="text-sm text-gray-600 mb-5" data-id="element-3143">
          Outbound call initiated to this customer.
        </p>
        <button onClick={onClose} className="w-full py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors" data-id="element-3144">
          OK
        </button>
      </div>
    </div>;
}
export function LeadsManagement() {
  const navigate = useNavigate();
  const {
    viewMode,
    currentLocation,
    isAllLocationsSelected,
    isSingleLocationSelected,
    selectedBrandLocations,
    getSelectedLocationData
  } = useViewMode();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [selectedChatLead, setSelectedChatLead] = useState<WhatsAppLead | null>(null);
  const [isDetailOverlayOpen, setIsDetailOverlayOpen] = useState(false);
  const [selectedDetailLead, setSelectedDetailLead] = useState<any>(null);
  const [isCallPopupOpen, setIsCallPopupOpen] = useState(false);
  const [isVoiceAgentOpen, setIsVoiceAgentOpen] = useState(false);
  const [voiceAgentLeadName, setVoiceAgentLeadName] = useState('');
  // Filter leads based on view mode and selected locations
  const getFilteredLeads = () => {
    if (viewMode === 'store' && currentLocation) {
      return ALL_LEADS.filter(lead => lead.dealerCode === currentLocation.code);
    }
    if (viewMode === 'brand') {
      if (isSingleLocationSelected) {
        const locationCode = selectedBrandLocations[0];
        return ALL_LEADS.filter(lead => lead.dealerCode === locationCode);
      }
      return ALL_LEADS;
    }
    return ALL_LEADS;
  };
  const filteredLeads = getFilteredLeads();
  const selectedLocation = getSelectedLocationData();
  const showLocationColumn = viewMode === 'brand' && isAllLocationsSelected;
  const handleOpenChat = (lead: Lead) => {
    const whatsappLead: WhatsAppLead = {
      id: lead.id,
      name: lead.name,
      phone: lead.phone,
      source: 'whatsapp',
      unreadCount: 0,
      aiScore: 75,
      status: 'active',
      tags: lead.tags || [],
      carModel: lead.carModel,
      dealer: lead.dealer
    };
    setSelectedChatLead(whatsappLead);
    setIsChatOpen(true);
  };
  const handleOpenDetailOverlay = (lead: Lead) => {
    setSelectedDetailLead({
      id: lead.id,
      name: lead.name,
      initials: lead.initials,
      email: lead.email,
      phone: lead.phone,
      callDuration: lead.recordingDuration || '---',
      leadType: lead.status,
      status: lead.type,
      carModel: lead.carModel
    });
    setIsDetailOverlayOpen(true);
  };
  const handleInitiateCall = (lead: Lead) => {
    if (lead.hasRecording) {
      setVoiceAgentLeadName(lead.name);
      setIsVoiceAgentOpen(true);
    } else {
      setIsCallPopupOpen(true);
    }
  };
  return <div className="flex w-full min-h-screen bg-[#f5f7fa]" data-id="element-3145">
      <Sidebar data-id="element-3146" />
      <main className="flex-1 flex flex-col h-screen overflow-hidden" data-id="element-3147">
        <Header data-id="element-3148" />
        <div className="flex-1 overflow-y-auto p-6" data-id="element-3149">
          <div className="flex items-center justify-between mb-6" data-id="element-3150">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-3151">Sales Leads</h1>
            {viewMode === 'brand' && isAllLocationsSelected && <span className="text-sm text-gray-500" data-id="element-3152">
                Showing {filteredLeads.length} leads across all locations
              </span>}
            {viewMode === 'brand' && isSingleLocationSelected && selectedLocation && <span className="inline-flex items-center px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-sm font-medium border border-amber-200" data-id="element-3153">
                  <MapPinIcon size={14} className="mr-1.5" data-id="element-3154" />
                  {selectedLocation.name} • {filteredLeads.length} leads
                </span>}
            {viewMode === 'store' && currentLocation && <span className="inline-flex items-center px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium" data-id="element-3155">
                <MapPinIcon size={14} className="mr-1.5" data-id="element-3156" />
                {currentLocation.name} • {filteredLeads.length} leads
              </span>}
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-id="element-3157">
            <div className={`grid ${showLocationColumn ? 'grid-cols-[48px_100px_1fr_1fr_1fr_180px]' : 'grid-cols-[48px_1fr_1fr_1fr_180px]'} border-b border-gray-200`} data-id="element-3158">
              <div className="p-4 flex items-center justify-center" data-id="element-3159">
                <input type="checkbox" className="w-4 h-4 rounded border-gray-300" data-id="element-3160" />
              </div>
              {showLocationColumn && <div className="p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3161">
                  Location
                </div>}
              <div className="p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3162">
                Lead Details
              </div>
              <div className="p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider border-l border-gray-200" data-id="element-3163">
                Contact Information
              </div>
              <div className="p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider border-l border-gray-200" data-id="element-3164">
                Dealer & Car Model
              </div>
              <div className="p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider border-l border-gray-200" data-id="element-3165">
                Actions
              </div>
            </div>

            {filteredLeads.map(lead => <div key={lead.id} className={`grid ${showLocationColumn ? 'grid-cols-[48px_100px_1fr_1fr_1fr_180px]' : 'grid-cols-[48px_1fr_1fr_1fr_180px]'} border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors`} data-id="element-3166">
                <div className="p-4 flex items-start justify-center pt-6 border-r border-gray-100" data-id="element-3167">
                  <input type="checkbox" className="w-4 h-4 rounded border-gray-300" data-id="element-3168" />
                </div>

                {showLocationColumn && <div className="p-4 border-r border-gray-100 flex items-start pt-5" data-id="element-3169">
                    <div className="inline-flex flex-col items-center px-2.5 py-1.5 bg-slate-100 rounded-lg" data-id="element-3170">
                      <MapPinIcon size={14} className="text-slate-500 mb-0.5" data-id="element-3171" />
                      <span className="text-[10px] font-bold text-slate-700" data-id="element-3172">
                        {lead.dealerCode}
                      </span>
                    </div>
                  </div>}

                <div className="p-4 border-r border-gray-100" data-id="element-3173">
                  {lead.hasAI && <div className="flex items-center gap-1 text-blue-600 text-xs font-medium mb-2" data-id="element-3174">
                      <SparklesIcon size={12} data-id="element-3175" />
                      <span data-id="element-3176">AI</span>
                    </div>}
                  <div className="flex items-center gap-3 mb-2" data-id="element-3177">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm" data-id="element-3178">
                      {lead.initials}
                    </div>
                    <div data-id="element-3179">
                      <p className="font-semibold text-gray-900 text-sm" data-id="element-3180">
                        {lead.name}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-3181">{lead.type}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap" data-id="element-3182">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${lead.status === 'cold' ? 'bg-blue-50 text-blue-600 border-blue-200' : lead.status === 'warm' ? 'bg-orange-50 text-orange-500 border-orange-200' : lead.status === 'hot' ? 'bg-red-50 text-red-500 border-red-200' : 'bg-green-50 text-green-600 border-green-200'}`} data-id="element-3183">
                      {lead.status.charAt(0).toUpperCase() + lead.status.slice(1)}
                    </span>
                    {lead.tags?.map((tag, idx) => <span key={idx} className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${tag === 'Completed' ? 'bg-green-50 text-green-600 border-green-200' : tag === 'Partial' ? 'bg-orange-50 text-orange-500 border-orange-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`} data-id="element-3184">
                        {tag}
                      </span>)}
                  </div>
                </div>

                <div className="p-4 border-r border-gray-100" data-id="element-3185">
                  <div className="space-y-1.5" data-id="element-3186">
                    <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-3187">
                      <MailIcon size={14} className="text-gray-400" data-id="element-3188" />
                      <span className="truncate text-xs" data-id="element-3189">{lead.email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-3190">
                      <PhoneIcon size={14} className="text-gray-400" data-id="element-3191" />
                      <span className="text-xs" data-id="element-3192">{lead.phone}</span>
                    </div>
                    {lead.callStatus === 'pending' && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-orange-50 text-orange-600 border border-orange-200" data-id="element-3193">
                        <span className="w-1 h-1 bg-orange-500 rounded-full" data-id="element-3194"></span>
                        Pending
                      </span>}
                    {lead.callStatus === 'callback-requested' && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-600 border border-blue-200" data-id="element-3195">
                        <span className="w-1 h-1 bg-blue-500 rounded-full" data-id="element-3196"></span>
                        Callback
                      </span>}
                  </div>
                </div>

                <div className="p-4 border-r border-gray-100" data-id="element-3197">
                  <div className="flex items-center gap-2 text-xs text-gray-600 mb-1.5" data-id="element-3198">
                    <BuildingIcon size={12} className="text-gray-400" data-id="element-3199" />
                    <span className="truncate" data-id="element-3200">{lead.dealer}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600 mb-2" data-id="element-3201">
                    <CarIcon size={12} className="text-gray-400" data-id="element-3202" />
                    <span data-id="element-3203">{lead.carModel}</span>
                  </div>
                  {lead.hasRecording && lead.recordingDuration ? <RecordingPlayer duration={lead.recordingDuration} leadId={lead.id} data-id="element-3204" /> : <div className="inline-flex px-2 py-1 bg-gray-100 rounded-full border border-gray-200" data-id="element-3205">
                      <span className="text-[10px] text-gray-500" data-id="element-3206">
                        No Recording
                      </span>
                    </div>}
                </div>

                <div className="p-4" data-id="element-3207">
                  {lead.multiVisit && <div className="flex justify-end mb-2" data-id="element-3208">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold bg-green-50 text-green-600 border border-green-200 uppercase" data-id="element-3209">
                        <RefreshCwIcon size={8} data-id="element-3210" />
                        Multi-Visit
                      </span>
                    </div>}
                  <div className="flex items-center justify-end gap-1.5" data-id="element-3211">
                    <button className="w-8 h-8 rounded-full border border-blue-200 flex items-center justify-center text-blue-500 hover:bg-blue-50 transition-colors" title="Schedule" data-id="element-3212">
                      <ClockIcon size={14} data-id="element-3213" />
                    </button>
                    <button onClick={() => handleInitiateCall(lead)} className="w-8 h-8 rounded-full border border-blue-200 flex items-center justify-center text-blue-500 hover:bg-blue-50 transition-colors" title="Call" data-id="element-3214">
                      <PhoneIcon size={14} data-id="element-3215" />
                    </button>
                    <button onClick={() => handleOpenChat(lead)} className="w-8 h-8 rounded-full border border-blue-200 flex items-center justify-center text-blue-500 hover:bg-blue-50 transition-colors" title="WhatsApp" data-id="element-3216">
                      <MessageCircleIcon size={14} data-id="element-3217" />
                    </button>
                    <button onClick={() => handleOpenDetailOverlay(lead)} className="w-8 h-8 rounded-full border border-blue-200 flex items-center justify-center text-blue-500 hover:bg-blue-50 transition-colors" title="Edit" data-id="element-3218">
                      <EditIcon size={14} data-id="element-3219" />
                    </button>
                  </div>
                  {lead.aiResponse && <p className="text-[10px] text-gray-500 mt-2 text-right" data-id="element-3220">
                      {lead.aiResponse}
                    </p>}
                  <p className="text-[10px] text-gray-400 mt-1 text-right" data-id="element-3221">
                    {lead.createdAt}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        <WhatsAppChatModal isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} lead={selectedChatLead} data-id="element-3222" />
        <LeadDetailOverlay isOpen={isDetailOverlayOpen} onClose={() => setIsDetailOverlayOpen(false)} lead={selectedDetailLead} data-id="element-3223" />
        <OutboundCallPopup isOpen={isCallPopupOpen} onClose={() => setIsCallPopupOpen(false)} data-id="element-3224" />
        <VoiceAgentOverlay isOpen={isVoiceAgentOpen} onClose={() => setIsVoiceAgentOpen(false)} leadName={voiceAgentLeadName} data-id="element-3225" />
      </main>
    </div>;
}